import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Typography, IconButton, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, CircularProgress, Card
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { v4 as uuidv4 } from 'uuid';
import { doc, setDoc, collection, getDocs, deleteDoc, writeBatch } from 'firebase/firestore';
import { BOARD_TEMPLATES } from '../../data/boardTemplatesData';
import { db } from '../../firebase';
import { initializeDashboardSuccess } from '../containers/Dashboard/actions/dashboardActions';
import DashboardEditor from '../../business/DashboardEditor/DashboardEditor';
import Dashboard from '../containers/Dashboard/Dashboard';

const PREVIEW_ORGANIZATION = {
  location: { lat: 31.7683, lng: 35.2137 },
  address: { city: 'ירושלים' }
};

const TemplateManager = () => {
  const dispatch = useDispatch();
  const catalog = useSelector(state => state.app.catalog);
  const dashboardState = useSelector(state => state.dashboard);
  const user = useSelector(state => state.auth?.user);
  const organization = useSelector(state => state.organization) || PREVIEW_ORGANIZATION;
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const [editingTemplate, setEditingTemplate] = useState(null);

  // דיאלוג שמירת תבנית
  const [saveDialog, setSaveDialog] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [importing, setImporting] = useState(false);

  // דיאלוג אישור מחיקה
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const templatesRef = collection(db, 'predefinedTemplates');
      const snapshot = await getDocs(templatesRef);
      const data = snapshot.docs.map(doc => {
        const d = { id: doc.id, ...doc.data() };
        if (d.widgets) {
          d.widgets = d.widgets.map((w, i) => ({
            ...w,
            widgetId: w.widgetId || `${doc.id}-w${i}`
          }));
        }
        return d;
      });
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he'));
      setTemplates(data);
    } catch (err) {
      setMessage('❌ שגיאה בטעינה: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '' });
    setSaveError('');
    setMessage('');
    dispatch(initializeDashboardSuccess({
      dashboardId: 'template-draft',
      organizationId: 'admin',
      name: 'תבנית חדשה',
      current: { widgets: [], settings: { bgColor: '#fcfbfa', bgImage: '' } },
      versions: []
    }));
    setView('editor');
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({ name: template.name || '' });
    setSaveError('');
    setMessage('');
    dispatch(initializeDashboardSuccess({
      dashboardId: 'template-draft',
      organizationId: 'admin',
      name: template.name || 'עריכת תבנית',
      current: {
        widgets: template.widgets || [],
        settings: template.settings || { bgColor: '#fcfbfa', bgImage: '' }
      },
      versions: []
    }));
    setView('editor');
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) return;
    setSaving(true);
    setSaveError('');
    try {
      const widgets = dashboardState.dashboard?.current?.widgets || [];
      const settings = dashboardState.dashboard?.current?.settings || { bgColor: '#fcfbfa', bgImage: '' };

      const templateId = editingTemplate?.id || uuidv4();
      const templateRef = doc(db, 'predefinedTemplates', templateId);

      const templateData = {
        id: templateId,
        name: templateForm.name.trim(),
        widgets,
        settings,
        updatedAt: new Date()
      };

      if (!editingTemplate) {
        templateData.createdAt = new Date();
      }

      await setDoc(templateRef, templateData, { merge: true });

      setSaveDialog(false);
      setView('list');
      setMessage('✅ התבנית נשמרה בהצלחה!');
      loadTemplates();
    } catch (err) {
      setSaveError('שגיאה בשמירה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImportPredefined = async () => {
    setImporting(true);
    try {
      const batch = writeBatch(db);
      BOARD_TEMPLATES.forEach(template => {
        const ref = doc(db, 'predefinedTemplates', template.id);
        batch.set(ref, { ...template, updatedAt: new Date() });
      });
      await batch.commit();
      setMessage(`✅ עודכנו ${BOARD_TEMPLATES.length} תבניות מוכנות`);
      loadTemplates();
    } catch (err) {
      setMessage('❌ שגיאה בייבוא: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleDeleteTemplate = async () => {
    const { id } = deleteConfirm;
    setDeleteConfirm({ open: false, id: null, name: '' });
    try {
      const templateRef = doc(db, 'predefinedTemplates', id);
      await deleteDoc(templateRef);
      setMessage('✅ נמחק בהצלחה');
      loadTemplates();
    } catch (err) {
      setMessage('❌ שגיאה: ' + err.message);
    }
  };

  // ===== EDITOR VIEW =====
  if (view === 'editor') {
    const saveTemplateBtn = (
      <Button
        variant="contained"
        onClick={() => setSaveDialog(true)}
        sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, whiteSpace: 'nowrap' }}
      >
        {editingTemplate ? 'עדכן תבנית' : 'שמור תבנית'}
      </Button>
    );

    return (
      <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, bgcolor: '#f5f7fa' }}>
        <DashboardEditor
          key={editingTemplate?.id || 'new-template'}
          user={user}
          organization={organization}
          dashboard={dashboardState.dashboard}
          hasUnsavedChanges={dashboardState.hasUnsavedChanges}
          isSaving={dashboardState.isSaving}
          dispatch={dispatch}
          catalog={catalog}
          history={dashboardState.history || []}
          historyIndex={dashboardState.historyIndex ?? -1}
          myWidgets={[]}
          savedWidgets={[]}
          templateMode={true}
          onBack={() => setView('list')}
          extraActions={saveTemplateBtn}
        />

        {/* דיאלוג שמירת תבנית */}
        <Dialog open={saveDialog} onClose={() => { setSaveDialog(false); setSaveError(''); }} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ direction: 'rtl' }}>פרטי התבנית</DialogTitle>
          <DialogContent sx={{ direction: 'rtl' }}>
            <TextField
              label="שם התבנית"
              fullWidth margin="normal"
              autoFocus
              value={templateForm.name}
              onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter' && templateForm.name.trim()) handleSaveTemplate(); }}
            />
            {saveError && (
              <Typography color="error" fontSize={13} sx={{ mt: 1 }}>{saveError}</Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
            <Button onClick={() => setSaveDialog(false)}>ביטול</Button>
            <Button
              variant="contained"
              onClick={handleSaveTemplate}
              disabled={saving || !templateForm.name.trim()}
              sx={{ bgcolor: '#1e3a5f' }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'שמור'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // ===== LIST VIEW =====
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color="#1e3a5f">
          ניהול תבניות מוכנות
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <CloudDownloadIcon />}
            onClick={handleImportPredefined}
            disabled={importing}
            sx={{ borderColor: '#1e3a5f', color: '#1e3a5f', '&:hover': { bgcolor: '#f0f4f8' } }}
          >
            ייבוא תבניות מוכנות
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewTemplate}
            sx={{ bgcolor: '#1e3a5f' }}
          >
            תבנית חדשה
          </Button>
        </Box>
      </Box>

      {message && (
        <Paper sx={{ p: 1.5, mb: 2, bgcolor: message.startsWith('✅') ? '#d4edda' : '#f8d7da' }}>
          <Typography fontSize={14}>{message}</Typography>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
          אין תבניות עדיין. צור תבנית חדשה!
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'flex-start' }}>
          {templates.map(template => (
            <Card
              key={template.id}
              onClick={() => handleEditTemplate(template)}
              sx={{ width: 280, border: '2px solid #e0e0e0', position: 'relative', cursor: 'pointer', '&:hover': { boxShadow: 4, borderColor: '#1e3a5f' } }}
            >
              {/* כפתור מחיקה בלבד - עצור propagation */}
              <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 10 }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => { e.stopPropagation(); setDeleteConfirm({ open: true, id: template.id, name: template.name }); }}
                  sx={{ bgcolor: 'rgba(255,255,255,0.9)' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* כותרת */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <Typography fontWeight={700} fontSize={15} color="#1e3a5f" noWrap>
                  {template.name}
                </Typography>
                <Typography fontSize={12} color="#64748b" noWrap>
                  {template.description || `${(template.widgets || []).length} ווידג'טים`}
                </Typography>
              </Box>

              {/* תצוגה מקדימה */}
              <div style={{ position: 'relative', direction: 'ltr', width: '100%',height: 180 }}>
                <Dashboard
                  user={user}
                  organization={organization}
                  dashboard={{ dashboardId: template.id, name: template.name, current: { widgets: template.widgets || [], settings: template.settings || {} }, versions: [] }}
                  dispatch={dispatch}
                  isEditMode={false}
                  onLogout={() => { }}
                  hasUnsavedChanges={false}
                  isSaving={false}
                />
              </div>
            </Card>
          ))}
        </Box>
      )}

      {/* דיאלוג אישור מחיקה */}
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null, name: '' })}>
        <DialogTitle sx={{ direction: 'rtl' }}>אישור מחיקה</DialogTitle>
        <DialogContent sx={{ direction: 'rtl' }}>
          <Typography>האם אתה בטוח שברצונך למחוק את &quot;{deleteConfirm.name}&quot;?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>פעולה זו אינה ניתנת לביטול.</Typography>
        </DialogContent>
        <DialogActions sx={{ direction: 'rtl', px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm({ open: false, id: null, name: '' })}>ביטול</Button>
          <Button variant="contained" color="error" onClick={handleDeleteTemplate}>מחק</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManager;
